import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, Output, OnInit, OnDestroy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';

import { isEmpty } from '@app/util/util';
import {
  HotelFilterApplier, HotelFilterApplierValue, HotelFilterInputTypes, HotelFilterTypes
} from '@shared/hotel-itinerary/hotel-filters';
import {
  DISABLED_FILTER_MULTIPLE_OPTIONS, HOTEL_FILTERS_PLACEHOLDER
} from '@hotels/hotel-results/constants/filters';
import { AppFacade } from '@app/app.facade';
import {
  PickedRange, FilterSorter, FilterSorterCriterias, PriceRangeFilter, PriceFilter
} from '@shared/filters/entities/fetch-filters.response';
import { CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { getPickedRangeLength } from '@shared/filters/mappers/range-filter.mapper';
import { getHotelItemLabel } from '@shared/filters/mappers/hotel-filters-label.mapper';
import { nightsLabel } from '@ux/mappers/guest.mapper';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { MODAL_HEIGHT } from '@app/constants/modal-heights';
import { HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { packHours } from '@shared/magic-filter-bar/byhours-filter-hour/constants/byhours.constant';

@Component({
  selector: 'app-hotels-filters',
  templateUrl: './hotels-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelsFiltersComponent implements OnInit, OnDestroy {

  @Input() isMobile: boolean;
  @Input() filters: HotelFilterVm[] = [];
  @Input() isLoading: boolean;
  @Input() pickedCheckboxes: string[] = [];
  @Input() pickedTotalDiscountPercentage: number;
  @Input() pickedStars: string[] = [];
  @Input() pickedRanges: PickedRange = {} as PickedRange;
  @Input() pickedSorter: FilterSorterCriterias = {} as FilterSorterCriterias;
  @Input() hotelNameSearch: string;
  @Input() hotelNights: number;
  @Input() exclusiveOffer: boolean;
  @Input() hasChangeSortByFilter = false;
  @Input() sorters: FilterSorter[] = [];
  @Input() hourType: string[];
  @Input() isByHours: boolean;

  @Output() filter: EventEmitter<HotelFilterApplier> = new EventEmitter<HotelFilterApplier>();
  @Output() clear: EventEmitter<void> = new EventEmitter<void>();
  @Output() sorter: EventEmitter<FilterSorter> = new EventEmitter<FilterSorter>();

  public quantity: number = 5;
  public isClearSelectedStars: boolean = false;
  public showSwipeUpInput: boolean = false;
  public modalHeight = MODAL_HEIGHT.FULL;
  public byHoursFilter = {
    type: HotelFilterTypes.PackHours,
    filter: HotelFilterInputTypes.Checkbox,
    value: packHours.map(hour => ({  
      Text: hour,
      Label: `${hour}h`
    })),
    order: 1
  };

  constructor(
    private translateService: TranslocoService,
    private domManipulateService: DomManipulateService,
    private route: ActivatedRoute,
    private appFacade: AppFacade,
  ) { }

  ngOnInit(): void {
    if (this.isMobile) {
      this.domManipulateService.setBodyScroll(false);
    }
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
  }

  get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  }

  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  }

  get hasActiveFilter(): boolean {
    const numberOfInput = !isEmpty(this.hotelNameSearch) ? 1 : 0;
    const numberOfRanges = this.getRangeLength(this.pickedRanges);
    const hasPickedCheckboxes = this.pickedCheckboxes.length + this.pickedStars.length;
    let totalCount = (hasPickedCheckboxes ? hasPickedCheckboxes + numberOfRanges :
      numberOfRanges === 0 ? null : numberOfRanges) + numberOfInput;
    if (this.pickedTotalDiscountPercentage && this.pickedTotalDiscountPercentage > 0) {
      totalCount++;
    }
    if (this.exclusiveOffer) {
      totalCount++;
    }
    return !!totalCount || this.hasChangeSortByFilter;
  }

  get source(): string {
    return this.route?.snapshot?.queryParams?.[SOURCE_PARAM];
  }

  public getRangeLength(ranges: PickedRange): number {
    return getPickedRangeLength(ranges);
  }

  public isCheckboxFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Checkbox && (item.value as CheckboxFilter[]).length > 0;
  }

  public isRadioFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Radio && (item.value as PriceRangeFilter[]).length > 0;
  }

  public isRangeFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Range &&
      (item.value as PriceFilter).min !== (item.value as PriceFilter).max;
  }

  public isInputFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Input;
  }

  public isStarsFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Stars && (item.value as CheckboxFilter[]).length > 0;
  }

  public isExclusiveOfferFilter(item: HotelFilterVm): boolean {
    return item.type === HotelFilterTypes.MemberRatesOnly && (item.value as CheckboxFilter[]).length > 0;
  }

  public getItemLabel(label: HotelFilterTypes): string {
    const keyTranslation = getHotelItemLabel(label);
    if (label === HotelFilterTypes.Price) {
      if (this.isByHours) {
        return this.translateService.translate('FLIGHT_RESULTS_MODULE.FILTER_SECTION.PRICE_HOURS');
      } else {
        return this.translateService.translate(keyTranslation, {
          nights: this.hotelNights,
          labelNights: `${this.translateService.translate(nightsLabel(this.hotelNights))}`.toLowerCase()
        });
      }
    } else {
      return this.translateService.translate(keyTranslation);
    }
  }

  public getPlaceholderToItem(label: string): string {
    return HOTEL_FILTERS_PLACEHOLDER[label];
  }

  public isDisabledMultipleOption(label: string): boolean {
    return DISABLED_FILTER_MULTIPLE_OPTIONS[label];
  }

  public applyFilter(type: HotelFilterTypes, value: HotelFilterApplierValue): void {
    this.filter.emit({ type, value });
  }

  public applyCheckboxFilter(filter: HotelFilterApplier): void {
    this.filter.emit(filter);
  }

  public applyByHoursFilter(filter: HotelFilterApplier): void {
    const _hourType = this.hourType.slice();
    const value = filter.value as string;
    if (filter.removable) {
      const index = _hourType.findIndex(hour => value === hour);
      _hourType.splice(index, 1);
    } else {
      _hourType.push(value);
    }
    this.filter.emit({...filter, value: _hourType});
  }

  public clearFilters(): void {
    if (this.hasActiveFilter) {
      this.pickedCheckboxes = [];
      this.pickedRanges = {};
      this.pickedStars = [];
      this.pickedTotalDiscountPercentage = null;
      this.clear.emit();
      this.clearSelectedStars(true);
      this.clearSelectedExclusiveOffer();
    }
  }

  public clearSelectedStars(value: boolean): void {
    this.isClearSelectedStars = value;
  }

  public clearSelectedExclusiveOffer(): void {
    const findFilter = this.filters.find(x => x.type === HotelFilterTypes.MemberRatesOnly);
    if (findFilter) {
      this.filter.emit({ type: <HotelFilterTypes>HotelFilterTypes.MemberRatesOnly.toString(), value: false });
    }
  }

  public swipeUpInput(value: boolean): void {
    this.showSwipeUpInput = value;
  }

  public trackById(index: number): number {
    return index;
  }

  public filterOrder(filter: HotelFilterVm): number {
    switch (true) {
      case this.isStarsFilter(filter):
        return 1;
      case this.isRangeFilter(filter):
        return 2;
      case this.isExclusiveOfferFilter(filter):
        return 3;
      case this.isRadioFilter(filter):
        return 4;
      default:
        return 5;
    }
  }

}
